<script setup lang="ts">
import type { IUseCreateUserArgs } from "@/api"
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { User } from "@/models"
import * as Sentry from "@sentry/vue"
import { toTypedSchema } from "@vee-validate/zod"
import { useUrlSearchParams } from "@vueuse/core"
import { useForm } from "vee-validate"
import { computed, onMounted, ref, useTemplateRef, watchEffect } from "vue"
import VueTurnstile from "vue-turnstile"
import { z } from "zod"
import { useCreateUser } from "@/api"
import { Button, Card, Input } from "@/components/ui"
import { useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { useAuthStore } from "@/stores/authStore"
import { passwordValidation } from "@/utils"

interface ProfileProps {
  canNext?: boolean
}

interface AuthProvider {
  id: string
  name: string
  icon: IconKeys
  onRegister: () => void
}

withDefaults(defineProps<ProfileProps>(), { canNext: true })

const emit = defineEmits<{
  userLoaded: []
  error: [error?: unknown]
  success: []
}>()

const appName = import.meta.env.VITE_APP_NAME ?? "Guestpix"

const authStore = useAuthStore()
const { t } = useTranslation()
const urlParams = useUrlSearchParams<{
  email?: string
  token?: string
  packageSubscriptionId?: unknown
  ReturnUrl?: unknown
  paymentToken?: string
}>()

const isPendingTurnstileCheck = ref<boolean>(true)
const validationErrors = ref<{ [key: string]: string[] }>()
const isFreemiumFlow = computed(
  () => !(urlParams.paymentToken ?? urlParams.token),
)
const isExternalAuthEnabled = computed<boolean>(() =>
  Boolean(import.meta.env.VITE_EXTERNAL_AUTH_ENABLED || false),
)

const loginRedirect = computed(() => {
  const base = "/onboard/setup"
  const params = new URLSearchParams()

  if (urlParams.token)
    params.set("token", urlParams.token as string)
  if (urlParams.paymentToken)
    params.set("paymentToken", urlParams.paymentToken as string)
  if (urlParams.packageSubscriptionId)
    params.set("packageSubscriptionId", String(urlParams.packageSubscriptionId))
  if (urlParams.ReturnUrl)
    params.set("ReturnUrl", urlParams.ReturnUrl as string)
  if (urlParams.email)
    params.set("email", urlParams.email as string)

  const queryString = params.toString()

  return queryString ? `${base}?${queryString}` : base
})

const showEmailForm = ref(!isExternalAuthEnabled.value)
const shouldShowEmailForm = computed(
  () => !isExternalAuthEnabled.value || showEmailForm.value,
)
const shouldShowExternalOptions = computed(
  () => isExternalAuthEnabled.value && !showEmailForm.value,
)

const turnstile = useTemplateRef("turnstile")
const turnstileToken = ref<string>("")
const turnstileSiteKey = computed(
  () => import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "",
)

const { mutate, isPending } = useCreateUser()

const validationSchema = toTypedSchema(
  z
    .object({
      name: z.string().min(1, t("onboarding.profile.provide_name")),
      email: z.string().email(t("onboarding.profile.invalid_email")),
      password: passwordValidation(),
      password_confirm: z
        .string()
        .min(8, t("onboarding.profile.invalid_password_length", { length: 8 })),
    })
    .refine(fields => fields.password === fields.password_confirm, {
      message: t("onboarding.profile.password_mismatch"),
      path: ["password_confirm"],
    }),
)

const { handleSubmit, setFieldValue } = useForm({
  validationSchema,
  initialValues: {
    name: "",
    email: "",
    password: "",
    password_confirm: "",
  },
})

function initialiseForm(user: User) {
  if (user.name)
    setFieldValue("name", user.name)
  if (user.email)
    setFieldValue("email", user.email)
}

const onSubmit = handleSubmit((values) => {
  const user = {
    name: values.name,
    email: values.email,
    password: values.password,
  }

  const paymentToken
    = urlParams.token && typeof urlParams.token === "string"
      ? urlParams.token
      : ""

  const mutationArgs: IUseCreateUserArgs = {
    user,
    paymentToken,
    turnstileToken: turnstileToken.value,
  }

  if (
    urlParams.packageSubscriptionId
    && typeof urlParams.packageSubscriptionId === "string"
  ) {
    mutationArgs.packageSubscriptionId = urlParams.packageSubscriptionId
  }

  mutate(mutationArgs, {
    async onSuccess(user: User) {
      emit("success")

      authStore.setUser(user)

      try {
        await authStore.signinSilent()
      }
      catch (err: unknown) {
        Sentry.withScope((scope) => {
          scope.setLevel("warning")
          scope.setTag("area", "oidc")

          scope.captureException(err)
        })
      }
    },
    onError(error) {
      if (error instanceof ValidationException) {
        validationErrors.value = error.validationErrors
      }
      emit("error", error)
    },
  })

  turnstileToken.value = ""
  turnstile.value?.reset()
})

function registerWithGoogle() {
  const currentPath = window.location.pathname
  const currentParams = new URLSearchParams(window.location.search)

  const returnUrl = `${currentPath}?${currentParams.toString()}`
  const loginRedirect = `${window.location.origin}/login?redirect=${encodeURIComponent(returnUrl)}`

  window.location.href = `${import.meta.env.VITE_BASE_API_URL}/identity/externallogin/challenge?provider=Google&returnUrl=${encodeURIComponent(loginRedirect)}&mode=onboard`
}

onMounted(() => {
  if (urlParams.email) {
    setFieldValue("email", urlParams.email)
    showEmailForm.value = true
  }
})

function onTurnstileExpired() {
  turnstileToken.value = ""
  turnstile.value?.reset()
}

function onTurnstileError(e: string) {
  emit("error", e)
}

watchEffect(() => {
  if (authStore.isAuthenticated && authStore.user) {
    emit("userLoaded")
    initialiseForm(authStore.user)
  }
})

watchEffect(() => {
  if (turnstileToken.value) {
    isPendingTurnstileCheck.value = false
  }
})

const authProviders: AuthProvider[] = [
  {
    id: "google",
    name: "Google",
    icon: "Google",
    onRegister: registerWithGoogle,
  },
]
</script>

<template>
  <Card>
    <template #header>
      <div class="mb-5 flex flex-col gap-1">
        <h1 class="font-heading font-bold text-2xl mb-2 capitalize">
          {{ t("onboarding.profile.title") }}
        </h1>
        <p class="font-body">
          {{ t("onboarding.thanks", { appName }) }}
        </p>
        <p class="font-body">
          {{ t("onboarding.profile.get_started", { appName }) }}
        </p>

        <i18n-t
          keypath="onboarding.profile.existing_user"
          scope="global"
          tag="p"
          class="font-body"
        >
          <template #link>
            <a
              data-test="login-link"
              :href="`/login?redirect=${encodeURIComponent(loginRedirect)}`"
              class="text-link hover:text-link-hover hover:underline focus:text-link-hover focus:underline"
            >{{ t("onboarding.profile.login_here") }}</a>
          </template>
        </i18n-t>
      </div>
    </template>
    <Transition name="gpx-fade" mode="out-in">
      <form v-show="shouldShowEmailForm" @submit.prevent="onSubmit">
        <div v-show="isPending" class="flex flex-col gap-5">
          <div class="gpx-skeleton h-12 w-full" />
          <div class="gpx-skeleton h-12 w-full" />
          <div class="gpx-skeleton h-12 w-full" />
          <div class="gpx-skeleton h-12 w-full" />
          <div class="gpx-skeleton h-12 w-40" />
        </div>
        <div v-show="!isPending" class="flex flex-col gap-5">
          <Button
            v-if="isExternalAuthEnabled"
            type="button"
            variant="tertiary"
            prefix-icon="ArrowLeft"
            size="sm"
            class="self-start"
            data-test="auth-options-back"
            @click="showEmailForm = false"
          >
            {{ t("onboarding.profile.back_to_sign_up_options") }}
          </Button>
          <Input name="name" :label="t('onboarding.profile.name_label')" />
          <Input
            name="email"
            type="email"
            :label="t('onboarding.profile.email_label')"
            :disabled="!isFreemiumFlow"
          />
          <Input
            name="password"
            type="password"
            :label="t('onboarding.profile.password_label')"
            :hint="t('onboarding.profile.password_hint')"
          />
          <Input
            name="password_confirm"
            type="password"
            :label="t('onboarding.profile.confirm_password_label')"
          />

          <VueTurnstile
            ref="turnstile"
            v-model="turnstileToken"
            :site-key="turnstileSiteKey"
            @error="onTurnstileError"
            @unsupported="onTurnstileError"
            @expired="onTurnstileExpired"
          />

          <div class="mt-8 flex justify-end gap-4">
            <Button
              size="lg"
              :disabled="isPendingTurnstileCheck"
              :suffix-icon="isPendingTurnstileCheck ? 'Loading' : undefined"
            >
              {{ t("onboarding.continue") }}
            </Button>
          </div>
        </div>
      </form>
    </Transition>

    <Transition name="gpx-fade" mode="out-in">
      <div v-show="shouldShowExternalOptions" class="mt-5 flex flex-col gap-6">
        <div class="flex items-center gap-3 py-1 text-gray-500">
          <div class="h-px flex-1 bg-gray-200" />
          <span
            class="font-heading text-xs font-semibold uppercase tracking-wide"
          >
            {{ t("onboarding.profile.or_continue_with") }}
          </span>
          <div class="h-px flex-1 bg-gray-200" />
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button
            v-for="provider of authProviders"
            :key="provider.id"
            :suffix-icon="provider.icon"
            @click="provider.onRegister"
          >
            {{ provider.name }}
          </Button>
          <Button
            variant="tertiary"
            suffix-icon="Mail"
            data-test="signup-with-email"
            @click="showEmailForm = true"
          >
            {{ t("onboarding.profile.sign_up_with_email") }}
          </Button>
        </div>
      </div>
    </Transition>
  </Card>
</template>
